import { ApiClientInstance, UtilsHelper } from "utils/helpers";
import { AuthEnums } from "models/enums";
import { WintSuperkollAPIModel as ApiModels } from "wint-api-client/dist/types";
import { Person } from "models/stateTypes/authState";
import { AxiosResponse } from "axios";

/**
 * @deprecated This service is deprecated. Use useAuthService hook instead.
 *
 * Migration guide:
 * 1. Replace AuthService calls with useAuthService hook
 * 2. Use the hook in React components or custom hooks
 * 3. Handle authentication state through Redux hooks
 *
 * @see useAuthService - The replacement hook for authentication functionality
 * @see https://app.clickup.com/4523827/v/dc/4a1tk-21632/4a1tk-8592
 *
 * @example
 * // Old way (deprecated):
 * import { AuthService } from "services";
 *
 * const loginResult = await AuthService.loginUser(userData);
 *
 * @example
 * // New way (recommended):
 * import useAuthService from "serviceHooks/useAuthService";
 *
 * function LoginComponent() {
 *   const { loginUser } = useAuthService();
 *   const loginResult = await loginUser(userData);
 * }
 */
const AuthService = {
  loginUser: (user: ApiModels.AuthPost) => {
    return ApiClientInstance(AuthEnums.AuthType.Unauthorized).AuthClient.PostJwt(user);
  },

  loginBankIdDesktopApp: async (
    isCancelled: () => boolean = () => false
  ): Promise<AxiosResponse<ApiModels.UserLoginInfo, any>> => {
    const bankIdClient = ApiClientInstance(AuthEnums.AuthType.Unauthorized).BankIdAuthClient;
    const initialStatus = (await bankIdClient.BeginLogin()).data;
    const loginToken = initialStatus.bankIdData?.signId ?? "";
    const autoStartToken = initialStatus.bankIdData?.autoStartToken ?? "";

    window.open(`bankid:///?autostarttoken=${autoStartToken}&redirect=null`, "_self");

    // eslint-disable-next-line no-constant-condition
    while (true) {
      await UtilsHelper.sleep(500);
      if (isCancelled()) {
        throw new Error("LOGIN CANCELLED");
      }
      const statusResponse = await bankIdClient.CheckLoginStatusJwt(loginToken);
      const status = statusResponse.data;
      if (!status.inProgress) {
        return {
          data: status.loginInfo ?? ({} as ApiModels.UserLoginInfo),
          status: 200,
          statusText: "",
          headers: statusResponse.headers,
          config: statusResponse.config,
        };
      }
    }
  },

  loginQrCode: async (
    cb: (qrStartToken: string, time: string, qrAuthCode: string) => void,
    isCancelled: () => boolean = () => false
  ): Promise<AxiosResponse<ApiModels.UserLoginInfo, any>> => {
    const bankIdClient = ApiClientInstance(AuthEnums.AuthType.Unauthorized).BankIdAuthClient;
    const initialStatus = (await bankIdClient.BeginLogin()).data;
    const loginToken = initialStatus.bankIdData?.signId ?? "";

    cb(initialStatus.bankIdData!.qRStartToken!, initialStatus.bankIdData!.time!, initialStatus.bankIdData!.qRAuthCode!);

    // eslint-disable-next-line no-constant-condition
    while (true) {
      await UtilsHelper.sleep(200);
      if (isCancelled()) {
        throw new Error("LOGIN CANCELLED");
      }
      const statusResponse = await bankIdClient.CheckLoginStatusJwt(loginToken);
      const status = statusResponse.data;
      if (!status.inProgress) {
        return {
          data: status.loginInfo ?? ({} as ApiModels.UserLoginInfo),
          status: 200,
          statusText: "",
          headers: statusResponse.headers,
          config: statusResponse.config,
        };
      }
      cb(status.bankIdData!.qRStartToken!, status.bankIdData!.time!, status.bankIdData!.qRAuthCode!);
    }
  },

  loginTicket: (ticket: string) => {
    return ApiClientInstance(AuthEnums.AuthType.Unauthorized).AuthClient.PostTicket(ticket);
  },

  mapLoggedInUserToPerson: (loggedInUser: ApiModels.UserLoginInfo) => {
    const person = new Person();
    person.key = loggedInUser.key ?? "";
    person.mail = loggedInUser.mail ?? "";
    person.name = loggedInUser.name ?? "";
    person.paymentMethods = (loggedInUser.paymentMethods ?? []).filter((pm) => !pm.isPaymentMethodTypeDisabled);
    person.personId = loggedInUser.personId;
    person.personNumber = loggedInUser.personNumber ?? "";
    person.receiptBankAccount = loggedInUser.receiptBankAccount ?? "";
    person.receiptBankClearing = loggedInUser.receiptBankAccountClearing ?? "";
    person.isHeartUser = loggedInUser.isHeartUser;
    person.authTokens = loggedInUser.authTokens ?? { accessToken: "", refreshToken: "" };
    person.hasMissingPaymentDetails = loggedInUser.hasMissingPaymentDetails;
    person.unsignedEula = loggedInUser.unsignedEula;
    person.canSuspendEula = loggedInUser.canSuspendEula;
    return person;
  },
};

export default AuthService;
